import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { hapticsController, VibrationContext } from "../../../haptics/haptics_controller.js";
import { HapticPattern } from "../../../haptics/haptics_constants.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import {
  MyTextAlign,
  MyTextVerticalAlign,
  UITextElement,
} from "../../common_ui_elements/ui_text_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { ConveyorConfigService } from "../../../conveyor_config/conveyor_config_service.js";
import { ConveyorTutorial, ConveyorTutorialStep } from "../../../conveyor/conveyor_tutorial.js";
import { TutorialCursor } from "../../common_ui_elements/tutorial_cursor.js";
import { TutorialSpotlight } from "../../common_ui_elements/tutorial_spotlight.js";

export type LoadingGameVariant = "default" | "conveyor";

interface ConveyorLayout
{
  logo: { y: number; w: number; h: number };
  level: { y: number; w: number; h: number; font: number };
  time: { y: number; w: number; h: number; font: number };
  picture: { y: number; w: number; h: number };
  shadow: { y: number; w: number; h: number };
  button: { y: number; w: number; h: number };
}

export class LoadingGameScreen extends UIElement
{
  private static readonly BACKGROUND_COLOR = "#4742D0";
  private static readonly GLOW_COLOR = "0, 120, 253";
  private static readonly ART_SCALE = 1.1;
  private static readonly ANIMAL_SIZE = 750;
  private static readonly ANIMAL_BELOW_CENTER = 150;
  private static readonly LOGO_W = 722;
  private static readonly LOGO_H = 356;
  private static readonly LOGO_GAP = 120;
  private static readonly SHADOW_W = 747;
  private static readonly SHADOW_H = 175;
  private static readonly SHADOW_OFFSET_Y = -110;
  private static readonly ANIMALS: ReadonlyArray<ImgID> = [
    ImgID.UI_GameLoadingAnimal1,
    ImgID.UI_GameLoadingAnimal2,
    ImgID.UI_GameLoadingAnimal3,
    ImgID.UI_GameLoadingAnimal4,
    ImgID.UI_GameLoadingAnimal5,
    ImgID.UI_GameLoadingAnimal6,
  ];

  private static readonly CONVEYOR_LOGO_W = 749;
  private static readonly CONVEYOR_LOGO_H = 422;
  private static readonly CONVEYOR_TEXT_W = 900;
  private static readonly CONVEYOR_LEVEL_H = 95;
  private static readonly CONVEYOR_LEVEL_FONT = 80;
  private static readonly CONVEYOR_TIME_H = 75;
  private static readonly CONVEYOR_TIME_FONT = 58;
  private static readonly CONVEYOR_PICTURE_SIZE = 680;
  private static readonly CONVEYOR_PICTURE_GAP = 30;
  private static readonly CONVEYOR_BUTTON_GAP = -30;
  private static readonly CONVEYOR_BUTTON_W = 640;
  private static readonly CONVEYOR_BUTTON_H = 270;
  private static readonly CONVEYOR_SHADOW_W = 640;
  private static readonly CONVEYOR_SHADOW_H = 150;
  private static readonly CONVEYOR_SHADOW_OFFSET_Y = -110;
  private static readonly CONVEYOR_CLOSE_SIZE = 130;
  private static readonly CONVEYOR_CLOSE_RIGHT = 60;
  private static readonly CONVEYOR_CLOSE_ABOVE_LOGO = 40;
  private static readonly CONVEYOR_BUTTON_FONT = 90;
  private static readonly CONVEYOR_LOADING_FONT = 80;
  private static readonly POP_UP_MS = 176;
  private static readonly POP_SET_MS = 112;
  private static readonly POP_SCALE = 1.15;
  private static readonly TUTORIAL_SPOTLIGHT_RADIUS_SCALE = 1;

  private static animal_id: ImgID = LoadingGameScreen.pickAnimal( null );
  private static variant: LoadingGameVariant = "default";

  public static setVariant( variant: LoadingGameVariant ): void
  {
    LoadingGameScreen.variant = variant;
  }

  private static pickAnimal( previous: ImgID | null ): ImgID
  {
    const options = LoadingGameScreen.ANIMALS.filter( id => id !== previous );
    return options[ Math.floor( Math.random() * options.length ) ];
  }

  public static rollAnimal(): void
  {
    LoadingGameScreen.animal_id = LoadingGameScreen.pickAnimal( LoadingGameScreen.animal_id );
  }

  // The artwork never animates, so one paint is enough — see update(). The art
  // is only held to tell whether that paint had every bitmap in hand.
  private art: UIImage[] = [];
  private art_settled = false;

  private glow_gradient: CanvasGradient | null = null;
  private glow_gradient_key = "";
  private animal_center_y = 0;
  private readonly is_conveyor: boolean;
  private events_controller: BasicEventsController | null = null;
  private on_choose: ( ( play: boolean ) => void ) | null = null;
  private loading_text: UITextElement | null = null;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.is_conveyor = LoadingGameScreen.variant === "conveyor";

    if ( this.is_conveyor )
      this.createConveyorArtwork();
    else
      this.createArtwork();
  }

  public isConveyor(): boolean
  {
    return this.is_conveyor;
  }

  public showPlayButtons( on_choose: ( play: boolean ) => void ): void
  {
    if ( this.on_choose )
      return;

    const layout = LoadingGameScreen.conveyorLayout( this.rect.getH() );
    const tutorial = ConveyorTutorial.getStep() === ConveyorTutorialStep.Play;

    this.on_choose = on_choose;
    this.hideLoadingText();

    if ( tutorial )
      this.createTutorialSpotlight( layout.button.y, layout.button.w, layout.button.h );

    this.createPlayButton( layout.button.y, layout.button.w, layout.button.h );

    if ( tutorial )
      this.createTutorialCursor( layout.button.y, layout.button.h );
    else
      this.createCloseButton( layout.logo.y );

    this.initializeEventsController();
  }

  private createTutorialSpotlight( y: number, w: number, h: number ): void
  {
    const x = this.rect.getW2() - w / 2;
    const target = { x, y, width: w, height: h };

    this.addChild( new TutorialSpotlight( this.ctx, this.rect.getW(), this.rect.getH(), target, LoadingGameScreen.TUTORIAL_SPOTLIGHT_RADIUS_SCALE ) );
  }

  private createTutorialCursor( y: number, h: number ): void
  {
    const tip_x = this.rect.getW2();
    const tip_y = y + h / 2;
    const cursor = new TutorialCursor( this.ctx );

    this.addChild( cursor );
    cursor.pointAt( new Vector2( tip_x, tip_y ) );
  }

  private hideLoadingText(): void
  {
    if ( !this.loading_text )
      return;

    this.removeChild( this.loading_text );
    this.loading_text = null;
  }

  private createPlayButton( y: number, w: number, h: number ): void
  {
    const x = this.rect.getW2() - w / 2;

    const button = new UINineSliceElement( this.ctx, x, y, w, h, ImgID.UI_GreenFrameBtn, "green_frame_btn", undefined, undefined, true );

    button.addChild( new UITextElement( this.ctx, 0, 0, w, h, {
      localization_key: "PLAY",
      font_size: LayoutScaler.textSizeFromDesign( LoadingGameScreen.CONVEYOR_BUTTON_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
      using_y_offset: false,
      outline_color: ColorScheme.menu_button_outline,
      ...TextStyleScheme.buttonShadow(),
    } ), true );

    button.setOnClick( () => this.choose( true ) );
    this.addChild( button );
    this.popIn( button );
  }

  private createCloseButton( logo_y: number ): void
  {
    const size = LayoutScaler.widthFromDesign( LoadingGameScreen.CONVEYOR_CLOSE_SIZE );
    const x = this.rect.getW() - LayoutScaler.widthFromDesign( LoadingGameScreen.CONVEYOR_CLOSE_RIGHT ) - size;
    const y = logo_y - LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_CLOSE_ABOVE_LOGO );

    const button = new UIImage( this.ctx, x, y, size, size, ImgID.UI_SettingsDialogCloseBtn, undefined, true );
    button.setOnClick( () => this.choose( false ) );
    this.addChild( button );
    this.popIn( button );
  }

  private popIn( element: UIImage | UINineSliceElement ): void
  {
    const start = performance.now();
    const zero = new Vector2( 0, 0 );
    const peak = new Vector2( LoadingGameScreen.POP_SCALE, LoadingGameScreen.POP_SCALE );
    const rest = new Vector2( 1, 1 );

    element.setScale( zero );
    element.addTransform( new TransformScale( element, true, zero, peak, start, LoadingGameScreen.POP_UP_MS, { easing: Easing.EaseOutQuad } ) );
    element.addTransform( new TransformScale( element, true, peak, rest, start + LoadingGameScreen.POP_UP_MS, LoadingGameScreen.POP_SET_MS, { easing: Easing.EaseOutQuad } ) );
  }

  private initializeEventsController(): void
  {
    this.events_controller?.cleanup();
    this.events_controller = new BasicEventsController( this.ctx.canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { this.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { this.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { this.onTouchMove( p, performance.now() ); }
    );
  }

  private choose( play: boolean ): void
  {
    const on_choose = this.on_choose;
    if ( !on_choose || !this.events_controller )
      return;

    this.events_controller.cleanup();
    this.events_controller = null;
    void hapticsController.vibrate( HapticPattern.LIGHT, VibrationContext.GENERAL );
    if ( play && ConveyorTutorial.getStep() === ConveyorTutorialStep.Play )
      ConveyorTutorial.setStep( ConveyorTutorialStep.Game );
    on_choose( play );
  }

  public cleanup(): void
  {
    this.events_controller?.cleanup();
    this.events_controller = null;
    super.cleanup();
  }

  private static conveyorLayout( height: number ): ConveyorLayout
  {
    const logo_w = LayoutScaler.widthFromDesign( LoadingGameScreen.CONVEYOR_LOGO_W );
    const logo_h = LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_LOGO_H );
    const text_w = LayoutScaler.widthFromDesign( LoadingGameScreen.CONVEYOR_TEXT_W );
    const level_h = LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_LEVEL_H );
    const time_h = LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_TIME_H );
    const picture_w = LayoutScaler.widthFromDesign( LoadingGameScreen.CONVEYOR_PICTURE_SIZE );
    const picture_h = LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_PICTURE_SIZE );
    const picture_gap = LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_PICTURE_GAP );
    const button_gap = LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_BUTTON_GAP );
    const button_w = LayoutScaler.widthFromDesign( LoadingGameScreen.CONVEYOR_BUTTON_W );
    const button_h = LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_BUTTON_H );
    const shadow_w = LayoutScaler.widthFromDesign( LoadingGameScreen.CONVEYOR_SHADOW_W );
    const shadow_h = LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_SHADOW_H );

    const total_h = logo_h + level_h + time_h + picture_gap + picture_h + button_gap + button_h;
    const logo_y = height / 2 - total_h / 2;
    const level_y = logo_y + logo_h;
    const time_y = level_y + level_h;
    const picture_y = time_y + time_h + picture_gap;
    const shadow_y = picture_y + picture_h + LayoutScaler.heightFromDesign( LoadingGameScreen.CONVEYOR_SHADOW_OFFSET_Y );
    const button_y = picture_y + picture_h + button_gap;

    return {
      logo: { y: logo_y, w: logo_w, h: logo_h },
      level: { y: level_y, w: text_w, h: level_h, font: LoadingGameScreen.CONVEYOR_LEVEL_FONT },
      time: { y: time_y, w: text_w, h: time_h, font: LoadingGameScreen.CONVEYOR_TIME_FONT },
      picture: { y: picture_y, w: picture_w, h: picture_h },
      shadow: { y: shadow_y, w: shadow_w, h: shadow_h },
      button: { y: button_y, w: button_w, h: button_h },
    };
  }

  private createConveyorArtwork(): void
  {
    const layout = LoadingGameScreen.conveyorLayout( this.rect.getH() );
    const level = StatisticsStorage.load().conveyor_level;
    const seconds = ConveyorConfigService.levelAt( level ).seconds;
    const level_text = LocalizationManager.tWithParams( "CONVEYOR.WAVE", [ String( level ) ] );
    const time_text = LocalizationManager.tWithParams( "CONVEYOR.HOLD_ON", [ String( seconds ) ] );

    this.addImage( ImgID.UI_GameLoadingShadow, layout.shadow.y, layout.shadow.w, layout.shadow.h );
    this.addImage( ImgID.UI_ConveyorLoadingPicture, layout.picture.y, layout.picture.w, layout.picture.h );
    this.addImage( ImgID.UI_ConveyorLoadingLogo, layout.logo.y, layout.logo.w, layout.logo.h );

    this.addText( { text: level_text }, layout.level.y, layout.level.w, layout.level.h, layout.level.font );
    this.addText( { text: time_text }, layout.time.y, layout.time.w, layout.time.h, layout.time.font );
    this.loading_text = this.addText( { localization_key: "LOADING" }, layout.button.y, layout.button.w, layout.button.h, LoadingGameScreen.CONVEYOR_LOADING_FONT );
  }

  private addText( content: { text?: string; localization_key?: string }, y: number, w: number, h: number, font: number ): UITextElement
  {
    const x = this.rect.getW2() - w / 2;
    const font_size = LayoutScaler.textSizeFromDesign( font );

    const text = new UITextElement( this.ctx, x, y, w, h, {
      ...content,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      font_size,
      font_weight: 900,
      auto_wrap: false,
      fit_font_size: true,
      shadow_offset_x: 0,
      shadow_offset_y: 0,
      outline_color: ColorScheme.booster_dialog_quantity_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } );
    text.setClickable( false );
    this.addChild( text );
    return text;
  }

  private createArtwork(): void
  {
    const scale = LoadingGameScreen.ART_SCALE;

    const animal_w = LayoutScaler.widthFromDesign( LoadingGameScreen.ANIMAL_SIZE * scale );
    const animal_h = LayoutScaler.heightFromDesign( LoadingGameScreen.ANIMAL_SIZE * scale );
    const animal_center_y = this.rect.getH2() + LayoutScaler.heightFromDesign( LoadingGameScreen.ANIMAL_BELOW_CENTER );
    const animal_y = animal_center_y - animal_h / 2;

    const logo_w = LayoutScaler.widthFromDesign( LoadingGameScreen.LOGO_W * scale );
    const logo_h = LayoutScaler.heightFromDesign( LoadingGameScreen.LOGO_H * scale );
    const logo_y = animal_y - LayoutScaler.heightFromDesign( LoadingGameScreen.LOGO_GAP ) - logo_h;

    const shadow_w = LayoutScaler.widthFromDesign( LoadingGameScreen.SHADOW_W * scale );
    const shadow_h = LayoutScaler.heightFromDesign( LoadingGameScreen.SHADOW_H * scale );
    const shadow_y = animal_y + animal_h + LayoutScaler.heightFromDesign( LoadingGameScreen.SHADOW_OFFSET_Y );

    this.animal_center_y = animal_center_y;

    this.addImage( ImgID.UI_GameLoadingShadow, shadow_y, shadow_w, shadow_h );
    this.addImage( LoadingGameScreen.animal_id, animal_y, animal_w, animal_h );
    this.addImage( ImgID.UI_LoadingLogo, logo_y, logo_w, logo_h );
  }

  private addImage( image_id: ImgID, y: number, w: number, h: number ): void
  {
    const x = this.rect.getW2() - w / 2;

    const image = new UIImage( this.ctx, x, y, w, h, image_id );
    image.setClickable( false );
    this.addChild( image );
    this.art.push( image );
  }

  private drawBackgroundAndGlow(): void
  {
    const w = this.rect.getW();
    const h = this.rect.getH();

    this.ctx.fillStyle = LoadingGameScreen.BACKGROUND_COLOR;
    this.ctx.fillRect( -w / 2, -h / 2, w, h );

    if ( this.is_conveyor )
      return;

    const glow_center_y = this.animal_center_y - h / 2;
    // render() is already translated to the screen centre, so x = 0 is centred.
    const glow_center_x = 0;
    const glow_radius = Math.min(
      LayoutScaler.widthFromDesign( 525 * LoadingGameScreen.ART_SCALE ),
      LayoutScaler.heightFromDesign( 525 * LoadingGameScreen.ART_SCALE )
    );
    const gradient_key = `${glow_center_x}|${glow_center_y}|${glow_radius}`;
    if ( !this.glow_gradient || this.glow_gradient_key !== gradient_key )
    {
      const gradient = this.ctx.createRadialGradient(
        glow_center_x,
        glow_center_y,
        0,
        glow_center_x,
        glow_center_y,
        glow_radius
      );
      gradient.addColorStop( 0, `rgba(${LoadingGameScreen.GLOW_COLOR}, 0.9)` );
      gradient.addColorStop( 0.55, `rgba(${LoadingGameScreen.GLOW_COLOR}, 0.55)` );
      gradient.addColorStop( 1, `rgba(${LoadingGameScreen.GLOW_COLOR}, 0)` );

      this.glow_gradient = gradient;
      this.glow_gradient_key = gradient_key;
    }

    this.ctx.fillStyle = this.glow_gradient;
    this.ctx.beginPath();
    this.ctx.arc( glow_center_x, glow_center_y, glow_radius, 0, Math.PI * 2 );
    this.ctx.fill();
  }

  // Kept for compatibility with loading-screen callers. This design has no
  // visible progress bar, so there is nothing to redraw for a progress change.
  public setProgress( _progress: number ): void
  {
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    if ( this.art_settled )
      return;

    this.invalidateSubtree();
    this.art_settled = this.art.every( image => ( image.getImage()?.width ?? 0 ) > 1 );
  }

  public name(): string
  {
    return "LoadingGameScreen";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.drawBackgroundAndGlow();
    this.finishRender();
  }
}
